/**
 * How one column of the grid becomes the column of the next frame. Pure, so it can be tested without a DOM.
 *
 * - keep: the same symbol, moving from row `from` of the old column
 * - crossfade: a mystery symbol of row `from` turning into a different symbol
 * - enter: a new symbol; it falls in from `drop` rows above its row, or appears in place when `drop` is 0
 */
export type CellPlan =
    | { kind: "keep"; from: number }
    | { kind: "crossfade"; from: number }
    | { kind: "enter"; drop: number };

export interface IColumnPlan {
    // By row of the new column
    cells: CellPlan[];
    // Rows of the old column that go away (fade out)
    removed: number[];
}

/**
 * The winning cells go, what is above them drops and the top of the column is filled with new symbols. Where the
 * column does not line up with the next frame (e.g. a mystery symbol changing) the cells are swapped one by one.
 */
export function planColumn(
    from: number[],
    to: number[],
    isWinning: (row: number) => boolean,
    isMystery: (id: number) => boolean
): IColumnPlan {
    const rows = to.length;
    const survivors: number[] = [];
    for (let row = 0; row < rows; row++) {
        if (!isWinning(row)) {
            survivors.push(row);
        }
    }
    const offset = rows - survivors.length;
    const drops = survivors.every(
        (row, i) => from[row] === to[offset + i] || isMystery(from[row])
    );

    const cells: CellPlan[] = [];
    const kept = new Set<number>();
    if (drops) {
        for (let row = 0; row < offset; row++) {
            cells.push({ kind: "enter", drop: offset });
        }
        survivors.forEach((row, i) => {
            cells.push({
                kind: from[row] === to[offset + i] ? "keep" : "crossfade",
                from: row
            });
            kept.add(row);
        });
    } else {
        for (let row = 0; row < rows; row++) {
            const stays = !isWinning(row);
            if (stays && from[row] === to[row]) {
                cells.push({ kind: "keep", from: row });
                kept.add(row);
            } else if (stays && isMystery(from[row])) {
                cells.push({ kind: "crossfade", from: row });
                kept.add(row);
            } else {
                cells.push({ kind: "enter", drop: 0 });
            }
        }
    }

    const removed: number[] = [];
    for (let row = 0; row < from.length; row++) {
        if (!kept.has(row)) {
            removed.push(row);
        }
    }
    return { cells, removed };
}
